import { useDispatch } from 'react-redux';
import { InputField, BasicSelectField } from 'lib/iterable-hook-form';
import { useCreateRoleMutation } from 'api/RolesAPI';
import { useRolesContext, NewRoleFormProvider } from 'modules/roles/contexts';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import {
  CREATE_ROLE_FORM_LABELS,
  CREATE_ROLE_TEXT,
  ROLE_NAME,
  INITIAL_PERMISSIONS,
} from 'modules/roles/constants';
import { createRolesOptions } from 'modules/roles/utils';
import { ApiError } from 'services/api';
import { FormField } from 'ui/formField';
import { PrimaryButton, BUTTON_SIZES } from 'ui/button';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { FieldValues, useFormContext } from 'react-hook-form';

type ModalProps = {
  onClose: () => void;
  onCreateSuccess: (name: string) => void;
};

const BaseCreateRoleModal = ({ onClose, onCreateSuccess }: ModalProps) => {
  const dispatch = useDispatch();
  const { combinedRoles, rolesById, refetchRoles } = useRolesContext();
  const rolesOptions = createRolesOptions(combinedRoles);

  const {
    handleSubmit,
    formState: { isValid, errors },
  } = useFormContext();

  const { mutate } = useCreateRoleMutation({
    onSuccess: (_, variables) => {
      dispatch(addPositiveToast('Success'));
      refetchRoles();
      onCreateSuccess(variables?.data?.name || '');
      onClose();
    },
    onError: (e: unknown) => {
      const { response } = e as ApiError;

      dispatch(
        addNegativeToast(
          `Unable to create role. ${response?.data?.message || 'Unknown error occured.'}`,
        ),
      );
      onClose();
    },
  });

  const onSubmit = (values: FieldValues) => {
    const selected = rolesById[values.initialPermissions as string];
    const payload = {
      name: values.roleName,
      permissions: selected?.permissions || [],
      scope: 'ProjectTeam',
    };
    mutate(payload);
  };

  return (
    <Modal
      id={CREATE_ROLE_TEXT.modalId}
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{CREATE_ROLE_TEXT.title}</ModalHeader>
      <ModalBody>
        <FormField
          labelText={CREATE_ROLE_FORM_LABELS[ROLE_NAME]}
          hasError={Boolean(errors[ROLE_NAME]?.message)}
          errorText={errors[ROLE_NAME]?.message as string | undefined}
        >
          <InputField name={ROLE_NAME} id={ROLE_NAME} autoFocus />
        </FormField>
        <FormField labelText={CREATE_ROLE_FORM_LABELS[INITIAL_PERMISSIONS]}>
          <BasicSelectField
            id={INITIAL_PERMISSIONS}
            name={INITIAL_PERMISSIONS}
            options={rolesOptions}
            placeholder={CREATE_ROLE_TEXT.rolesOptionsPlaceholder}
            isFullWidth={true}
          />
        </FormField>
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <PrimaryButton
          isDisabled={!isValid}
          onClick={handleSubmit(onSubmit)}
          size={BUTTON_SIZES.LARGE}
        >
          {CREATE_ROLE_TEXT.buttonLabel}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};

export const CreateRoleModal = ({ onClose, onCreateSuccess }: ModalProps) => (
  <NewRoleFormProvider>
    <BaseCreateRoleModal onClose={onClose} onCreateSuccess={onCreateSuccess} />
  </NewRoleFormProvider>
);
